import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import api from "./../api";
import { PageHeader } from "./../components/ui";

const VENV_COLOR = { ready: "success", missing: "warning", provisioning: "info", error: "error" };

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || ""); // strip data: prefix
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Playground() {
  const [gens, setGens] = useState([]);
  const [selected, setSelected] = useState("");

  // image inputs
  const [prompt, setPrompt] = useState("");
  const [inputImage, setInputImage] = useState(null); // File
  const [optionsText, setOptionsText] = useState("");
  // audio inputs
  const [audioFile, setAudioFile] = useState(null); // File
  const [language, setLanguage] = useState("");

  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [imageUrl, setImageUrl] = useState(null); // object URL
  const [text, setText] = useState("");

  // Live job log (polled from the scheduler, like the Rebuild build log).
  const [jobLog, setJobLog] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const logRef = useRef(null);
  const atBottom = useRef(true);

  const load = useCallback(() => {
    api.get("/api/generators", { silent: true }).then((d) => setGens(d.generators)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // Only enabled generators whose venv is ready can be selected/run.
  const readyGens = useMemo(() => gens.filter((g) => g.venv_status === "ready" && g.enabled), [gens]);
  const gen = useMemo(() => readyGens.find((g) => g.name === selected) || null, [readyGens, selected]);

  // Default to the first ready generator; drop the selection if it stops being ready.
  useEffect(() => {
    if (readyGens.length && !readyGens.some((g) => g.name === selected)) {
      setSelected(readyGens[0].name);
    } else if (!readyGens.length && selected) {
      setSelected("");
    }
  }, [readyGens, selected]);

  const resetOutput = () => {
    setError("");
    setText("");
    setJobLog("");
    setJobStatus("");
    atBottom.current = true;
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  // Auto-scroll the live log to the newest line unless the user scrolled up.
  useEffect(() => {
    const el = logRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [jobLog]);
  const onLogScroll = () => {
    const el = logRef.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  // Poll a submitted job's live log until it finishes, then fetch its output.
  const pollJob = async (id, mediaType) => {
    for (;;) {
      let d;
      try {
        d = await api.get(`/api/playground/jobs/${id}`, { silent: true });
      } catch (e) {
        setError(e.message || "Lost track of the job.");
        return;
      }
      setJobLog(d.log || "");
      setJobStatus(d.status || "");
      if (d.done) {
        if (d.status === "done") {
          if (mediaType === "image") {
            const blob = await api.get(`/api/playground/jobs/${id}/result`, { blob: true, silent: true });
            setImageUrl(URL.createObjectURL(blob));
          } else {
            const r = await api.get(`/api/playground/jobs/${id}/result`, { silent: true });
            setText(r.text || "(no text returned)");
          }
        } else {
          setError(d.error || "Generation failed — see the log below.");
        }
        load();  // refresh warm/cold state — this generator is now hot-loaded
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
  };

  // Reset outputs when switching generator.
  useEffect(() => {
    resetOutput();
  }, [selected]);

  const runImage = async () => {
    resetOutput();
    setRunning(true);
    try {
      let options = {};
      if (optionsText.trim()) {
        try {
          options = JSON.parse(optionsText);
        } catch {
          throw new Error("Options must be valid JSON.");
        }
      }
      const body = { prompt, options };
      if (inputImage) body.image = await fileToBase64(inputImage);
      const { job_id } = await api.post(`/api/playground/${selected}/generate`, body, { silent: true });
      await pollJob(job_id, "image");
    } catch (e) {
      setError(e.message || "Generation failed");
    } finally {
      setRunning(false);
    }
  };

  const runAudio = async () => {
    resetOutput();
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append("file", audioFile);
      if (language.trim()) fd.append("language", language.trim());
      const { job_id } = await api.post(`/api/playground/${selected}/transcribe`, fd, { silent: true });
      await pollJob(job_id, "audio-stt");
    } catch (e) {
      setError(e.message || "Transcription failed");
    } finally {
      setRunning(false);
    }
  };

  const isImage = gen?.media_type === "image";
  const isAudio = gen?.media_type === "audio-stt";
  const canRun =
    !running && !!gen && ((isImage && (prompt.trim() || inputImage)) || (isAudio && audioFile));

  return (
    <Box>
      <PageHeader
        eyebrow="Generate"
        title="Playground"
        subtitle="Run a generator by hand — prompt an image model or transcribe an audio clip — through the same scheduler as production traffic."
      />

      <Grid container spacing={2}>
        {/* Input column */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <TextField
                select
                fullWidth
                size="small"
                label="Generator"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={readyGens.length === 0}
                helperText={
                  readyGens.length === 0
                    ? "No enabled, ready generators — enable one and build its venv (Generators)."
                    : undefined
                }
                sx={{ mb: 1.5 }}
              >
                {readyGens.map((g) => (
                  <MenuItem key={g.id} value={g.name}>
                    {g.name} · {g.media_type}
                  </MenuItem>
                ))}
                {readyGens.length === 0 && <MenuItem disabled>No enabled, ready generators</MenuItem>}
              </TextField>

              {gen && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  {gen.venv_name && (
                    <Chip
                      label={`venv: ${gen.venv_status}`}
                      size="small"
                      color={VENV_COLOR[gen.venv_status] || "default"}
                      sx={{ height: 20, fontSize: ".7rem" }}
                    />
                  )}
                  {gen.warm ? (
                    <Chip label="⚡ warm" size="small" color="info"
                          title="Model is hot-loaded — this run skips the reload"
                          sx={{ height: 20, fontSize: ".7rem", fontWeight: 600 }} />
                  ) : (
                    <Chip label="cold" size="small" variant="outlined"
                          title="First run loads the model into VRAM (slower); repeats stay warm"
                          sx={{ height: 20, fontSize: ".7rem", color: "text.secondary" }} />
                  )}
                  {gen.description && (
                    <Typography variant="caption" color="text.secondary">
                      {gen.description}
                    </Typography>
                  )}
                </Stack>
              )}

              {isImage && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    label="Prompt"
                    placeholder="a red fox in a snowy forest, cinematic lighting"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    sx={{ mb: 1.5 }}
                  >
                    {inputImage ? inputImage.name : "Input image (optional)"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => setInputImage(e.target.files?.[0] || null)}
                    />
                  </Button>
                  <Accordion sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" color="text.secondary">
                        Advanced options (JSON)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        placeholder={'{ "steps": 4, "guidance": 0 }'}
                        value={optionsText}
                        onChange={(e) => setOptionsText(e.target.value)}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: ".8rem" } }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              {isAudio && (
                <>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    sx={{ mb: 1.5, display: "block" }}
                  >
                    {audioFile ? audioFile.name : "Audio file"}
                    <input
                      hidden
                      type="file"
                      accept="audio/*,.wav,.mp3,.m4a,.flac,.ogg"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  <TextField
                    size="small"
                    label="Language (optional)"
                    placeholder="en"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    sx={{ mb: 1.5, width: 160 }}
                  />
                </>
              )}

              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                  disabled={!canRun}
                  onClick={isImage ? runImage : runAudio}
                >
                  {running ? "Running…" : "Run"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Output column */}
        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 200 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Output
              </Typography>

              {(running || jobLog) && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Job log</Typography>
                    {running && <CircularProgress size={13} />}
                    {jobStatus && (
                      <Chip
                        size="small"
                        label={jobStatus}
                        color={jobStatus === "error" ? "error" : jobStatus === "done" ? "success" : "info"}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                  <Box
                    component="pre"
                    ref={logRef}
                    onScroll={onLogScroll}
                    sx={{
                      m: 0,
                      height: 260,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                      fontSize: "0.76rem",
                      lineHeight: 1.55,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "#0e1b2e",
                      color: "#cdd7e6",
                    }}
                  >
                    {jobLog || (running ? "Submitting…" : "")}
                    {running && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.25,
                          "@keyframes gpBlink": { "0%,50%": { opacity: 1 }, "50.01%,100%": { opacity: 0 } },
                          animation: "gpBlink 1s step-end infinite",
                        }}
                      >
                        ▋
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}

              {!running && imageUrl && (
                <Box sx={{ mt: 1 }}>
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="result"
                    sx={{ maxWidth: "100%", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      component="a"
                      href={imageUrl}
                      download={`${selected}.png`}
                      size="small"
                      startIcon={<DownloadIcon />}
                    >
                      Download
                    </Button>
                  </Box>
                </Box>
              )}

              {!running && text && (
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                    bgcolor: "background.default",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {text}
                </Box>
              )}

              {!running && !error && !imageUrl && !text && !jobLog && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Run a generator to see its output here.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
